import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Wallet, Calculator, Shield, TrendingUp } from 'lucide-react';
import Disclaimer from '../../components/Disclaimer';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const GererBankroll = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/guides" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux guides
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comment Gérer son Bankroll comme un Professionnel
          </h1>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              La gestion du bankroll est la compétence la plus importante pour réussir dans les paris à long terme. Peu importe votre 
              capacité à analyser les matchs ou à identifier les bonnes cotes : si vous ne gérez pas votre capital correctement, vous 
              finirez par perdre. Ce guide vous enseigne les principes fondamentaux de la gestion de bankroll professionnelle.
            </p>

            <div className="bg-purple-50 border-l-4 border-purple-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Wallet className="w-6 h-6 text-purple-600" />
                Définir Votre Bankroll Initiale
              </h2>
              <p>
                Votre bankroll est le montant total que vous êtes prêt à consacrer aux paris, et que vous pouvez vous permettre de perdre 
                sans impact sur votre vie quotidienne. Ne jamais utiliser de l'argent nécessaire pour les dépenses essentielles (loyer, 
                nourriture, factures). Votre bankroll doit être un capital de loisir, pas une source de stress financier.
              </p>
              <p>
                Une fois votre bankroll définie, considérez-la comme un capital d'investissement. Ne la "rechargez" pas après des pertes 
                sans avoir réfléchi à ce qui n'a pas fonctionné. Si vous perdez votre bankroll complète, c'est un signal que vous devez 
                revoir votre stratégie, pas simplement injecter plus d'argent. La discipline commence par définir et respecter vos limites.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              La Règle d'Or : Ne Jamais Miser Plus de 5%
            </h2>
            <p>
              La règle fondamentale de la gestion de bankroll est simple : ne jamais miser plus de 2-5% de votre bankroll totale sur un 
              seul pari. Même si vous êtes absolument certain de gagner, la variance existe toujours. Un pari à 90% de chances de gagner 
              perdra quand même 10% du temps, et si vous avez misé 50% de votre bankroll, vous êtes ruiné.
            </p>
            <p>
              Les professionnels utilisent généralement des mises entre 1% et 3% de leur bankroll. Les mises de 4-5% sont réservées aux 
              situations exceptionnelles où vous avez une très forte valeur et une très haute confiance. Les mises supérieures à 5% sont 
              considérées comme du jeu compulsif, pas de la gestion professionnelle.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calculator className="w-6 h-6 text-blue-600" />
                Calculer Vos Mises selon le Niveau de Confiance
              </h2>
              <p>
                Tous vos paris ne méritent pas la même mise. Développez un système de mise proportionnelle basé sur votre niveau de 
                confiance et la valeur perçue. Par exemple : 1% pour les paris exploratoires ou les tests de nouvelles stratégies, 
                2% pour les paris avec valeur moyenne et confiance modérée, 3% pour les paris avec bonne valeur et confiance élevée, 
                4-5% maximum pour les paris exceptionnels avec très forte valeur et très haute confiance.
              </p>
              <p>
                Ce système vous permet de maximiser vos gains sur vos meilleurs paris tout en limitant les pertes sur les paris moins 
                certains. Mais attention : ne surestimez pas votre confiance. Beaucoup de parieurs pensent avoir "trouvé le bon coup" 
                et augmentent leurs mises, pour découvrir ensuite que leur analyse était incorrecte. Restez humble et discipliné.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Gérer les Séquences de Gains et de Pertes
            </h2>
            <p>
              Les séquences de gains et de pertes sont normales dans les paris. Même avec une stratégie gagnante, vous connaîtrez des 
              périodes de pertes. La clé est de ne pas paniquer et de ne pas changer votre stratégie de mise pendant ces périodes. 
              Si votre système est bon, il finira par fonctionner sur le long terme.
            </p>
            <p>
              Pendant une séquence de gains, résistez à la tentation d'augmenter vos mises parce que "vous êtes en forme". Votre bankroll 
              augmente, donc vos mises en pourcentage augmentent automatiquement. Si vous augmentez aussi le pourcentage, vous prenez 
              des risques excessifs. Pendant une séquence de pertes, ne "doublez" jamais pour rattraper : c'est le chemin le plus rapide 
              vers la ruine totale.
            </p>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-green-600" />
                Ajuster Votre Bankroll selon les Performances
              </h2>
              <p>
                Votre bankroll n'est pas fixe : elle évolue selon vos gains et pertes. Recalculez régulièrement vos mises en pourcentage 
                basées sur votre bankroll actuelle, pas sur votre bankroll initiale. Si vous avez gagné 50% et que votre bankroll est 
                passée de 1000€ à 1500€, vos mises de 3% passent de 30€ à 45€ automatiquement.
              </p>
              <p>
                Si vous perdez et que votre bankroll diminue, réduisez vos mises en conséquence. Ne maintenez pas des mises élevées sur 
                une bankroll réduite : cela augmente votre risque de ruine. Certains parieurs professionnels vont même jusqu'à réduire 
                temporairement leur pourcentage de mise après des pertes importantes pour protéger leur capital restant.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Éviter le Piège du "Rattrapage"
            </h2>
            <p>
              Le piège le plus dangereux est la tentation de "rattraper" une perte en augmentant vos mises. Cette approche, appelée 
              "martingale" ou "chasing losses", est mathématiquement vouée à l'échec. Même si vous finissez par gagner, vous aurez 
              risqué une somme disproportionnée pour récupérer une perte initiale modeste.
            </p>
            <p>
              Si vous perdez plusieurs paris d'affilée, c'est peut-être le moment de faire une pause, de revoir votre stratégie, ou 
              de réduire temporairement vos mises. Ne jamais augmenter vos mises pour compenser des pertes : c'est la différence 
              entre un parieur discipliné et un joueur compulsif.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
                Fixer des Objectifs Réalistes
              </h2>
              <p>
                Fixez-vous des objectifs de gains réalistes basés sur votre bankroll et votre niveau d'expertise. Un objectif de 10-20% 
                de gain mensuel est ambitieux mais réalisable pour un parieur compétent. Des objectifs de 50% ou 100% mensuels sont 
                irréalistes et vous pousseront à prendre des risques excessifs.
              </p>
              <p>
                Si vous atteignez vos objectifs mensuels, considérez retirer une partie de vos gains pour sécuriser vos profits. Ne 
                laissez pas tout votre capital sur le site : retirez régulièrement une partie de vos gains pour construire une réserve 
                en dehors de votre bankroll de paris. Cette approche protège vos profits et réduit la tentation de tout remiser.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tenir un Journal de Vos Paris
            </h2>
            <p>
              Un journal détaillé de tous vos paris est essentiel pour améliorer votre gestion de bankroll. Notez chaque pari : le montant 
              misé, le type de pari, votre niveau de confiance, le résultat, et surtout, pourquoi vous avez pris cette décision. 
              Analysez régulièrement ce journal pour identifier les patterns : sur quels types de paris vous réussissez le mieux ? 
              Où perdez-vous le plus ?
            </p>
            <p>
              Le journal vous aide aussi à éviter les erreurs répétées. Si vous constatez que vous perdez systématiquement sur un type 
              de pari ou dans certaines situations, vous pouvez ajuster votre stratégie. Sans journal, vous répétez les mêmes erreurs 
              sans en prendre conscience. La gestion de bankroll professionnelle commence par la connaissance de soi et de ses performances.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Notre Approche Recommandée
            </h2>
            <p>
              La gestion de bankroll efficace repose sur trois piliers : discipline, patience, et méthode. Définissez une bankroll 
              que vous pouvez vous permettre de perdre, ne misez jamais plus de 2-5% sur un seul pari, ajustez vos mises selon votre 
              niveau de confiance, recalculez régulièrement basé sur votre bankroll actuelle, évitez absolument le rattrapage, et 
              tenez un journal détaillé de tous vos paris.
            </p>
            <p>
              Rappelez-vous : même les meilleurs parieurs perdent régulièrement. La différence entre les gagnants et les perdants à long 
              terme n'est pas dans le pourcentage de paris gagnants, mais dans la gestion du capital. Ceux qui réussissent sont ceux 
              qui peuvent survivre aux séquences de pertes grâce à une gestion prudente, et qui maximisent leurs gains pendant les 
              séquences positives grâce à une discipline constante. Votre bankroll est votre outil le plus précieux : protégez-la, 
              gérez-la intelligemment, et elle vous récompensera sur le long terme.
            </p>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default GererBankroll;

